import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import Page from '../../components/Page';
import Breadcrumb from '../../components/Breadcrumb';
import client from '../../lib/feathers';

/**
 * Everything one PERSON has done, across every browser they have used.
 *
 * The visitor profile answers "what has this browser done", which is the right
 * question about a device and the wrong one about a human. A visitor is the
 * vs_vid cookie, so one person with a phone, a laptop and a cleared cookie is
 * three visitors and their history is split across three pages, none of which
 * shows it. Measured on production 2026-09-22: one staff account held 7
 * visitor ids covering 33 identified visits across the app and the web.
 *
 * Anonymous sessions on a claimed browser are shown, and marked. Reading the
 * public site before signing up is part of the story; it is just not a claim
 * that we knew who they were at the time.
 */

function Stat({ label, value, hint }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="text-lg text-gray-900">{value}</dd>
      {hint && <dd className="text-xs text-gray-400">{hint}</dd>}
    </div>
  );
}

function duration(sec) {
  if (!sec) return '0s';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export default function UserJourney() {
  const { userId } = useParams();
  const [journey, setJourney] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await client.service('admin/user-journey').get(userId);
        if (!cancelled) { setJourney(r); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this history');
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const s = journey?.summary;

  return (
    <Page>
      <Breadcrumb items={[
        { label: 'Analytics', to: '/analytics' },
        { label: 'Person' },
      ]}
      />

      {error && <p className="text-sm text-red-700">{error}</p>}
      {!journey && !error && <p className="text-sm text-gray-400">Loading...</p>}

      {journey && (
        <>
          <h1 className="text-2xl text-gray-900">{journey.user.username || journey.user.email}</h1>
          <p className="text-xs text-gray-400 font-mono mb-4">{journey.user.id}</p>

          <dl className="bg-white shadow rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-4">
            <Stat label="Devices" value={s.visitorCount} hint="browsers they have signed in on" />
            <Stat label="Visits" value={s.visitCount} hint={s.anonVisitCount ? `${s.anonVisitCount} before signing in` : undefined} />
            <Stat label="Page views" value={s.eventCount} />
            <Stat label="Actions" value={s.actionCount} />
            <Stat label="Total time" value={duration(s.totalSeconds)} />
            <Stat
              label="First seen"
              value={s.firstSeen ? formatDistanceToNow(new Date(s.firstSeen), { addSuffix: true }) : '-'}
            />
          </dl>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Devices</h3>
              {journey.visitors.map((v) => (
                <p key={v.id} className="text-sm text-gray-600">
                  <Link className="text-indigo-600 hover:underline font-mono text-xs" to={`/analytics/visitors/${v.id}`}>
                    {v.id.slice(0, 8)}
                  </Link>
                  {' '}
                  {(v.os || []).join(', ') || 'unknown'}
                  {' '}
                  <span className="text-gray-400">
                    ({(v.surfaces || []).join(' + ') || '-'}, {v.visitCount} visit{v.visitCount === 1 ? '' : 's'})
                  </span>
                </p>
              ))}
            </div>

            <div className="bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Also seen on these devices</h3>
              {journey.alsoSeen.length === 0 && (
                <p className="text-sm text-gray-400 italic">Nobody else has signed in on them</p>
              )}
              {journey.alsoSeen.map((u) => (
                <p key={u.id} className="text-sm text-gray-600">
                  <Link className="text-indigo-600 hover:underline" to={`/analytics/people/${u.id}`}>{u.username}</Link>
                  {' '}
                  <span className="text-gray-400">({u.visitCount} visit{u.visitCount === 1 ? '' : 's'})</span>
                </p>
              ))}
              {journey.alsoSeen.length > 0 && (
                // The honest caveat on this whole page: a shared device means
                // some of what is below may be somebody else's.
                <p className="mt-2 text-xs text-amber-700">
                  Shared device. Some visits below may belong to them, not to this person.
                </p>
              )}
            </div>
          </div>

          <h2 className="text-lg text-gray-900 mb-2">Visits</h2>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">From</th>
                  <th className="px-4 py-3">Device</th>
                  <th className="px-4 py-3">Surface</th>
                  <th className="px-4 py-3">Did</th>
                  <th className="px-4 py-3">For</th>
                  <th className="px-4 py-3">Who</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {journey.visits.map((v) => (
                  <tr key={v.id} className="text-sm text-gray-700">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {format(new Date(v.startedAt), 'd MMM HH:mm')}
                    </td>
                    <td className="px-4 py-3">{v.channel || '-'}</td>
                    <td className="px-4 py-3">
                      {v.os || '-'} <span className="text-gray-400">{v.deviceType || ''}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{v.surface || '-'}</td>
                    <td className="px-4 py-3">
                      {v.eventCount} view{v.eventCount === 1 ? '' : 's'}, {v.actionCount} action{v.actionCount === 1 ? '' : 's'}
                    </td>
                    <td className="px-4 py-3">{duration(v.durationSec)}</td>
                    <td className="px-4 py-3">
                      {v.wasAnonymous ? (
                        // Claimed because it happened on a browser they were
                        // later identified on, NOT because we knew at the time.
                        <span className="text-xs text-gray-500 italic">not signed in</span>
                      ) : (
                        v.username || '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Page>
  );
}
